import { createSignal, createEffect, createMemo, untrack, For, Index, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { A, useNavigate, useParams } from '@solidjs/router';
import Text from '../components/Text';
import SellbotNav from '../components/SellbotNav';
import SellbotOfferPicker from '../components/SellbotOfferPicker';
import QuantityInput from '../components/QuantityInput';
import TextWithCopyButton from '../components/TextWithCopyButton';
import { SkeletonCard } from '../components/Skeleton';
import { useToast } from '../components/MessageBox';
import { useVillagerOfferText } from '../components/VillagerOfferPicker';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime, fromDateTimeInput, toDateTimeInput } from '../services/dateFormat';
import { formatQuantity } from '../services/quantityFormatter';
import { sellbotStatusClass } from '../services/sellbotStatus';
import type { SellbotGrantRedemptionDto, SellbotOfferDto } from '../api';
import sellbotStyles from './Sellbot.module.css';
import cardStyles from './SellbotHolograms.module.css';
import styles from './SellbotGrant.module.css';

// The sale task gathers the whole grant into the bot's main inventory and hotbar before dropping it.
const MAX_STACKS = 36;

type DraftLine = { sellbotOfferId: string; itemCount: number };

export default function SellbotGrant() {
    const api = useApi();
    const navigate = useNavigate();
    const params = useParams();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();
    const offerText = useVillagerOfferText();

    const isNew = () => params.grantId === 'new';

    const [loading, setLoading] = createSignal(true);
    const [saving, setSaving] = createSignal(false);
    const [offers, setOffers] = createSignal<SellbotOfferDto[]>([]);
    const [compressedRetrieval, setCompressedRetrieval] = createSignal(false);
    const [savedCode, setSavedCode] = createSignal('');
    const [redemptions, setRedemptions] = createSignal<SellbotGrantRedemptionDto[]>([]);

    const [draft, setDraft] = createStore({
        label: '',
        code: '',
        boundPlayerName: '',
        maxRedemptions: 1,
        oncePerPlayer: false,
        expiresAt: '',
        customerMessage: '',
        lines: [{ sellbotOfferId: '', itemCount: 1 }] as DraftLine[],
    });

    const load = async () => {
        const id = botId();
        if (!id) { setLoading(false); return; }

        const cfg = await api.sellbot.getConfig(id);
        if (isOk(cfg.data)) {
            setOffers(cfg.data.value.offers);
            setCompressedRetrieval(cfg.data.value.allowCompressedChamberRetrieval);
        }

        if (!isNew()) {
            const [list, history] = await Promise.all([
                api.sellbotGrant.list(id),
                api.sellbotGrant.redemptions(id, params.grantId),
            ]);
            const grant = isOk(list.data) ? list.data.value.find(g => g.id === params.grantId) : undefined;
            if (!grant) { toast?.error(t('SELLBOT_GRANT_TOAST_NOT_FOUND')); navigate('/sellbot/grants'); return; }
            setSavedCode(grant.code);
            setDraft({
                label: grant.label,
                code: grant.code,
                boundPlayerName: grant.boundPlayerName ?? '',
                maxRedemptions: grant.maxRedemptions,
                oncePerPlayer: grant.oncePerPlayer,
                expiresAt: toDateTimeInput(grant.expiresAt),
                customerMessage: grant.customerMessage ?? '',
                lines: grant.lines.map(line => ({ sellbotOfferId: line.sellbotOfferId, itemCount: line.itemCount })),
            });
            if (isOk(history.data)) setRedemptions(history.data.value);
        }

        setLoading(false);
    };

    createEffect(() => {
        botId();
        setLoading(true);
        untrack(() => { void load(); });
    });

    const offerOf = (offerId: string) => offers().find(offer => offer.id === offerId);

    const chosenOfferIds = createMemo(() => draft.lines.map(line => line.sellbotOfferId).filter(Boolean));

    const stacksNeeded = createMemo(() => draft.lines.reduce((total, line) => {
        const offer = offerOf(line.sellbotOfferId);
        if (!offer) return total;
        return total + Math.ceil(line.itemCount / Math.max(1, offerText.stackSizeOf(offer.item)));
    }, 0));

    const stacksOver = () => !compressedRetrieval() && stacksNeeded() > MAX_STACKS;

    const addLine = () => setDraft('lines', draft.lines.length, { sellbotOfferId: '', itemCount: 1 });
    const removeLine = (lineIndex: number) => setDraft('lines', draft.lines.filter((_, index) => index !== lineIndex));

    const save = async () => {
        const id = botId();
        if (!id) return;

        const lines = draft.lines.filter(line => line.sellbotOfferId && line.itemCount > 0);
        if (lines.length === 0) { toast?.error(t('SELLBOT_GRANT_TOAST_NEEDS_ITEM')); return; }

        const request = {
            label: draft.label.trim(),
            code: draft.code.trim() || null,
            boundPlayerName: draft.boundPlayerName.trim() || null,
            maxRedemptions: draft.maxRedemptions,
            oncePerPlayer: draft.maxRedemptions > 1 && draft.oncePerPlayer,
            expiresAt: fromDateTimeInput(draft.expiresAt)?.toISOString() ?? null,
            customerMessage: draft.customerMessage.trim() || null,
            lines,
        };

        setSaving(true);
        const res = isNew()
            ? await api.sellbotGrant.create(id, request)
            : await api.sellbotGrant.update(id, params.grantId, request);
        setSaving(false);

        if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('SELLBOT_GRANT_TOAST_SAVE_FAILED')); return; }
        toast?.success(t('SELLBOT_GRANT_TOAST_SAVED'));
        navigate('/sellbot/grants');
    };

    return (
        <div class={sellbotStyles.container}>
            <SellbotNav />

            <div class={sellbotStyles.headerRow}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <h1><Text val={isNew() ? 'SELLBOT_GRANT_NEW_TITLE' : 'SELLBOT_GRANT_EDIT_TITLE'} /></h1>
                    <span class={sellbotStyles.heroSubtitle}><Text val="SELLBOT_GRANT_EDITOR_SUBTITLE" /></span>
                </div>
                <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                    <button class="btn btn-secondary" onClick={() => navigate('/sellbot/grants')}>
                        <Text val="SELLBOT_GRANT_CANCEL" />
                    </button>
                    <button class="btn btn-primary" onClick={() => void save()} disabled={saving() || loading() || !draft.label.trim() || stacksOver()}>
                        <i class={saving() ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-floppy-disk'} />
                        <Text val="SELLBOT_GRANT_SAVE" />
                    </button>
                </div>
            </div>

            <Show when={!loading()} fallback={<div class="v" style={{ gap: 'var(--spacing-md)' }}><SkeletonCard /><SkeletonCard /></div>}>
                <div class={cardStyles.editorLayout}>
                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                            <div class="h hcenter" style={{ 'justify-content': 'space-between' }}>
                                <h2 class={cardStyles.sectionTitle} style={{ margin: '0' }}><Text val="SELLBOT_GRANT_SECTION_ITEMS" /></h2>
                                <button class="btn btn-secondary" onClick={addLine}>
                                    <i class="fa-solid fa-plus" />
                                    <Text val="SELLBOT_GRANT_ADD_ITEM" />
                                </button>
                            </div>

                            <Index each={draft.lines}>
                                {(line, lineIndex) => (
                                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <div class={styles.lineRow}>
                                            <SellbotOfferPicker
                                                offers={offers().filter(offer => offer.id === line().sellbotOfferId || !chosenOfferIds().includes(offer.id))}
                                                value={line().sellbotOfferId}
                                                onChange={(offerId) => setDraft('lines', lineIndex, 'sellbotOfferId', offerId)}
                                                placeholder={t('SELLBOT_GRANT_PICK_OFFER')} />
                                            <QuantityInput inline min={1} value={line().itemCount}
                                                stackSize={offerOf(line().sellbotOfferId) ? offerText.stackSizeOf(offerOf(line().sellbotOfferId)!.item) : 64}
                                                onChange={(itemCount) => setDraft('lines', lineIndex, 'itemCount', itemCount)}
                                                placeholder={t('SELLBOT_GRANT_F_AMOUNT')} />
                                            <button class="btn btn-icon btn-danger" title={t('SELLBOT_GRANT_REMOVE_ITEM')}
                                                onClick={() => removeLine(lineIndex)} disabled={draft.lines.length === 1}>
                                                <i class="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                        <Show when={offerOf(line().sellbotOfferId)}>
                                            {(offer) => (
                                                <Show when={offer().tiers.length > 0}
                                                    fallback={<span class={styles.warning}><Text val="SELLBOT_GRANT_NO_TIER" /></span>}>
                                                    <span class={offer().availableStock < line().itemCount ? styles.warning : cardStyles.cardMeta}>
                                                        {offer().availableStock < line().itemCount
                                                            ? t('SELLBOT_GRANT_STOCK_SHORT', formatQuantity(offer().availableStock))
                                                            : t('SELLBOT_GRANT_STOCK', formatQuantity(offer().availableStock))}
                                                    </span>
                                                </Show>
                                            )}
                                        </Show>
                                    </div>
                                )}
                            </Index>

                            <Show when={!compressedRetrieval()}>
                                <span class={stacksOver() ? styles.warning : cardStyles.cardMeta}>
                                    {stacksOver()
                                        ? t('SELLBOT_GRANT_STACKS_OVER', String(stacksNeeded()), String(MAX_STACKS))
                                        : t('SELLBOT_GRANT_STACKS', String(stacksNeeded()), String(MAX_STACKS))}
                                </span>
                            </Show>
                        </div>

                        <Show when={!isNew()}>
                            <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                <h2 class={cardStyles.sectionTitle} style={{ margin: '0' }}><Text val="SELLBOT_GRANT_SECTION_REDEMPTIONS" /></h2>
                                <Show when={redemptions().length > 0} fallback={
                                    <div class="v hcenter" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center' }}>
                                        <i class="fa-solid fa-inbox" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                                        <p class={cardStyles.cardMeta}><Text val="SELLBOT_GRANT_REDEMPTIONS_EMPTY" /></p>
                                    </div>
                                }>
                                    <For each={redemptions()}>
                                        {(redemption) => (
                                            <A href={`/sellbot/transactions/${redemption.transactionId}`} class={styles.redemptionRow}>
                                                <span>{redemption.playerName}</span>
                                                <span class={`${sellbotStyles.badge} ${sellbotStatusClass(redemption.status, sellbotStyles)}`}>
                                                    {t('SELLBOT_TXN_' + redemption.status)}
                                                </span>
                                                <span class={cardStyles.cardMeta}>{formatDateTime(redemption.createdAt)}</span>
                                            </A>
                                        )}
                                    </For>
                                    <Show when={redemptions().some(redemption => redemption.status === 'Failed')}>
                                        <span class={cardStyles.sectionHint}><Text val="SELLBOT_GRANT_REDEMPTIONS_FAILED_HINT" /></span>
                                    </Show>
                                </Show>
                            </div>
                        </Show>
                    </div>

                    <form class="fcard v" style={{ gap: 'var(--spacing-md)' }} onSubmit={(e) => { e.preventDefault(); void save(); }}>
                        <h2 class={cardStyles.sectionTitle} style={{ margin: '0' }}><Text val="SELLBOT_GRANT_SECTION_RULES" /></h2>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_GRANT_FIELD_LABEL" /></span>
                            <input type="text" maxLength={64} value={draft.label} onInput={(e) => setDraft('label', e.currentTarget.value)} />
                            <span class={cardStyles.cardMeta}><Text val="SELLBOT_GRANT_FIELD_LABEL_HINT" /></span>
                        </label>

                        <Show when={isNew()} fallback={
                            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="SELLBOT_GRANT_FIELD_CODE" /></span>
                                <TextWithCopyButton text={savedCode()} />
                                <span class={cardStyles.cardMeta}><Text val="SELLBOT_GRANT_CODE_FIXED_HINT" /></span>
                            </div>
                        }>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="SELLBOT_GRANT_FIELD_CODE" /></span>
                                <input type="text" class={styles.codeInput} maxLength={16} value={draft.code}
                                    placeholder={t('SELLBOT_GRANT_CODE_PLACEHOLDER')}
                                    onInput={(e) => setDraft('code', e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} />
                                <span class={cardStyles.cardMeta}><Text val="SELLBOT_GRANT_FIELD_CODE_HINT" /></span>
                            </label>
                        </Show>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_GRANT_FIELD_PLAYER" /></span>
                            <input type="text" maxLength={16} value={draft.boundPlayerName}
                                placeholder={t('SELLBOT_GRANT_FIELD_PLAYER_PLACEHOLDER')}
                                onInput={(e) => setDraft('boundPlayerName', e.currentTarget.value)} />
                            <span class={cardStyles.cardMeta}><Text val="SELLBOT_GRANT_FIELD_PLAYER_HINT" /></span>
                        </label>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_GRANT_FIELD_MAX" /></span>
                            <input type="number" min="1" value={draft.maxRedemptions}
                                onInput={(e) => setDraft('maxRedemptions', Math.max(1, Math.floor(Number(e.currentTarget.value) || 1)))} />
                            <span class={cardStyles.cardMeta}><Text val="SELLBOT_GRANT_FIELD_MAX_HINT" /></span>
                        </label>

                        <Show when={draft.maxRedemptions > 1}>
                            <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                <input type="checkbox" checked={draft.oncePerPlayer} onChange={(e) => setDraft('oncePerPlayer', e.currentTarget.checked)} />
                                <Text val="SELLBOT_GRANT_FIELD_ONCE" />
                            </label>
                        </Show>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_GRANT_FIELD_EXPIRES" /></span>
                            <input type="datetime-local" value={draft.expiresAt} onInput={(e) => setDraft('expiresAt', e.currentTarget.value)} />
                            <span class={cardStyles.cardMeta}><Text val="SELLBOT_GRANT_FIELD_EXPIRES_HINT" /></span>
                        </label>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="SELLBOT_GRANT_FIELD_MESSAGE" /></span>
                            <input type="text" maxLength={256} value={draft.customerMessage}
                                onInput={(e) => setDraft('customerMessage', e.currentTarget.value)} />
                            <span class={cardStyles.cardMeta}><Text val="SELLBOT_GRANT_FIELD_MESSAGE_HINT" /></span>
                        </label>
                    </form>
                </div>
            </Show>
        </div>
    );
}
